import React, { useState, useEffect } from 'react';
import {
  getShippingBoxes,
  getShippingBox,
  updateShippingBoxStatus,
  createAdjustment,
  getPendingAdjustments,
  approveRejectAdjustment,
} from '../api';

function Receiving({ user }) {
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [pendingAdjustments, setPendingAdjustments] = useState([]);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({
    product_id: '',
    quantity_change: '',
    reason: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadBoxes();
    if (user.role === 'admin') {
      loadPendingAdjustments();
    }
  }, [user.role]);

  const loadBoxes = async () => {
    try {
      const response = await getShippingBoxes();
      setBoxes(response.data.filter(box => box.status !== 'received'));
    } catch (err) {
      setError('Failed to load shipping boxes');
    }
  };

  const loadPendingAdjustments = async () => {
    try {
      const response = await getPendingAdjustments();
      setPendingAdjustments(response.data);
    } catch (err) {
      console.error('Failed to load pending adjustments');
    }
  };

  const viewBox = async (boxId) => {
    try {
      const response = await getShippingBox(boxId);
      setSelectedBox(response.data);
    } catch (err) {
      setError('Failed to load box details');
    }
  };

  const markAsReceived = async (boxId) => {
    try {
      await updateShippingBoxStatus(boxId, 'received');
      setMessage('Box marked as received');
      setSelectedBox(null);
      loadBoxes();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const requestAdjustment = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      await createAdjustment({
        ...adjustmentData,
        box_id: selectedBox.id,
        quantity_change: parseInt(adjustmentData.quantity_change),
      });

      setMessage('Adjustment request submitted for admin approval');
      setShowAdjustmentForm(false);
      setAdjustmentData({ product_id: '', quantity_change: '', reason: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request adjustment');
    }
  };

  const handleAdjustmentApproval = async (adjustmentId, status) => {
    try {
      await approveRejectAdjustment(adjustmentId, status);
      setMessage(`Adjustment ${status}`);
      loadPendingAdjustments();
    } catch (err) {
      setError(`Failed to ${status} adjustment`);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">Receiving</div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {user.role === 'admin' && pendingAdjustments.length > 0 && (
          <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '5px' }}>
            <h3 style={{ marginBottom: '10px' }}>Pending Adjustments</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Change</th>
                    <th>Reason</th>
                    <th>Requested By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingAdjustments.map((adj) => (
                    <tr key={adj.id}>
                      <td>{adj.product_name}</td>
                      <td>{adj.quantity_change > 0 ? '+' : ''}{adj.quantity_change}</td>
                      <td>{adj.reason || '-'}</td>
                      <td>{adj.requested_by_name}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="btn btn-success"
                            onClick={() => handleAdjustmentApproval(adj.id, 'approved')}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleAdjustmentApproval(adj.id, 'rejected')}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!selectedBox ? (
          <>
            <h3 style={{ marginBottom: '15px' }}>Shipments to Receive</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Box Number</th>
                    <th>Tracking</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {boxes.map((box) => (
                    <tr key={box.id}>
                      <td>{box.box_number}</td>
                      <td>{box.tracking_number || '-'}</td>
                      <td>
                        <span className={`badge badge-${box.status}`}>
                          {box.status}
                        </span>
                      </td>
                      <td>{new Date(box.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn btn-primary"
                          onClick={() => viewBox(box.id)}
                          style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setSelectedBox(null)}
              style={{ marginBottom: '20px' }}
            >
              Back to List
            </button>

            <h3>Box: {selectedBox.box_number}</h3>
            {selectedBox.tracking_number && (
              <p>Tracking: {selectedBox.tracking_number}</p>
            )}
            {selectedBox.notes && (
              <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px', marginTop: '10px' }}>
                <strong>Notes:</strong> {selectedBox.notes}
              </div>
            )}

            <h4 style={{ marginTop: '20px', marginBottom: '15px' }}>Items</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Description</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBox.items.filter(item => item.product_id).map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.description || '-'}</td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="btn-group" style={{ marginTop: '20px' }}>
              <button
                className="btn btn-success"
                onClick={() => markAsReceived(selectedBox.id)}
              >
                Mark as Received
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAdjustmentForm(!showAdjustmentForm)}
              >
                Report Damage/Issue
              </button>
            </div>

            {showAdjustmentForm && (
              <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                <h4>Request Inventory Adjustment</h4>
                <form onSubmit={requestAdjustment}>
                  <div className="form-group">
                    <label>Product *</label>
                    <select
                      value={adjustmentData.product_id}
                      onChange={(e) =>
                        setAdjustmentData({
                          ...adjustmentData,
                          product_id: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">-- Select Product --</option>
                      {selectedBox.items.filter(item => item.product_id).map((item) => (
                        <option key={item.product_id} value={item.product_id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quantity Change * (use negative for damage)</label>
                    <input
                      type="number"
                      value={adjustmentData.quantity_change}
                      onChange={(e) =>
                        setAdjustmentData({
                          ...adjustmentData,
                          quantity_change: e.target.value,
                        })
                      }
                      required
                      placeholder="e.g., -2 for 2 damaged items"
                    />
                  </div>

                  <div className="form-group">
                    <label>Reason *</label>
                    <textarea
                      value={adjustmentData.reason}
                      onChange={(e) =>
                        setAdjustmentData({
                          ...adjustmentData,
                          reason: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="btn-group">
                    <button type="submit" className="btn btn-primary">
                      Submit Request
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowAdjustmentForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Receiving;
